import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { IntegrationProvider } from 'api/integrations';
import { ProjectId, TaskId, TaskIdV2 } from 'api/models';
import { NotFound } from 'components/atoms/result';
import { SecondaryTabs } from 'components/organisms/task-pane/secondary-tabs/secondary-tabs';
import {
  ConversationsCol,
  Drawer,
  LoadingContainer,
  Row,
  TaskCol,
} from 'components/organisms/task-pane/styled';
import { useProjectTasksSocketUpdates } from 'components/pages/use-project-tasks-socket-updates';
import { useTaskDrawerContext } from 'contexts/providers/task-pane';
import { useTaskDetailStore } from 'data/tasks/hooks/use-task-detail-store';
import { useFieldsLoaded } from 'hooks/use-fields-loaded';
import { ApiErrorType } from 'queries/error';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { TaskDetail } from '../task-pane/task-detail';
import { HeaderContentLoader } from '../task-pane/task-detail/header/header-content-loader';
import { TASK_CONVERSATION_PANE_WIDTH } from '../tasks/constants';
import { useTaskDrawer } from './hash-based-task-drawer';
import { JiraIssueDrawer } from './jira-issue-drawer';

export interface ContentProps {
  projectId: ProjectId;
  taskId: TaskIdV2;
  onClose(): void;
}

const DrawerContent = ({
  projectId: [, projectId],
  taskId: [, taskId],
  onClose,
}: ContentProps) => {
  const _taskId = useMemo(() => TaskId(projectId, taskId), [projectId, taskId]);
  const { data, isLoading, error } = useTaskDetailStore(_taskId);

  const [mimicLoading, setMimicLoading] = useState(true);

  const { data: jiraConfigResponse } = useQueryJiraConfig();

  const jiraConfig = useMemo(() => {
    if (jiraConfigResponse?.length) {
      return jiraConfigResponse[0];
    }

    return undefined;
  }, [jiraConfigResponse]);

  useProjectTasksSocketUpdates(ProjectId(projectId));
  const [showJiraIssueCreation, setShowJiraIssueCreation] = useState(false);

  const canRenderJiraIssueDrawer = useMemo(() => {
    if (isLoading) {
      return false;
    }

    if (data && data.linkedResources) {
      const integrationProviders = data.linkedResources?.map(
        ({ externalIntegrationType }) => externalIntegrationType,
      );

      return !integrationProviders?.includes(IntegrationProvider.Jira);
    }

    return false;
  }, [data, isLoading]);

  const handleJiraIssueCreate = useCallback(
    () => setShowJiraIssueCreation(true),
    [],
  );

  const onJiraIssueDrawerClose = useCallback(
    () => setShowJiraIssueCreation(false),
    [],
  );

  useEffect(() => {
    if (data?.taskId) {
      setMimicLoading(true);
      setTimeout(() => {
        setMimicLoading(false);
      }, 500);
    }
  }, [data?.taskId]);

  if (isLoading || mimicLoading) {
    return (
      <LoadingContainer>
        <HeaderContentLoader />
      </LoadingContainer>
    );
  }

  return (
    <>
      {data ? (
        <>
          <Row $private={data.private}>
            <TaskCol flex="1 1 595px">
              <TaskDetail
                data={data}
                onDelete={onClose}
                handleJiraIssueCreate={handleJiraIssueCreate}
                jiraConfig={jiraConfig}
              />
            </TaskCol>
            <ConversationsCol flex={`0 1 ${TASK_CONVERSATION_PANE_WIDTH}px`}>
              <Suspense fallback={null}>
                <SecondaryTabs data={data} />
              </Suspense>
            </ConversationsCol>
          </Row>
          {canRenderJiraIssueDrawer && jiraConfig != null ? (
            <JiraIssueDrawer
              showDrawer={showJiraIssueCreation}
              taskData={data}
              jiraConfig={jiraConfig}
              onClose={onJiraIssueDrawerClose}
            />
          ) : null}
        </>
      ) : error ? (
        error.response?.data.type === ApiErrorType.RecordDeleted ? (
          <NotFound
            title={<FormattedMessage id="task.404.record_deleted.title" />}
            subTitle={<FormattedMessage id="task.404.record_deleted.message" />}
          />
        ) : (
          <NotFound subTitle={<FormattedMessage id="task.404.message" />} />
        )
      ) : null}
    </>
  );
};

export const TaskDrawer = () => {
  const [params, , hide] = useTaskDrawer();
  const [projectId, setProjectId] = useState<ProjectId | null>();
  const [taskId, setTaskId] = useState<TaskIdV2 | null>();
  const { isFieldsLoaded } = useFieldsLoaded();
  const { visibility, setVisibility } = useTaskDrawerContext();

  const onClose = useCallback(() => {
    setVisibility(false);
    setTimeout(hide, 100);
  }, [hide, setVisibility]);

  useEffect(() => {
    switch (params[0]) {
      case 'show':
        setVisibility(true);
        setProjectId(params[1]);
        setTaskId(params[2]);
        break;

      case 'invalid':
      default:
        setProjectId(null);
        setTaskId(null);
        setVisibility(false);
    }
  }, [params, setVisibility]);

  return (
    <Drawer onClose={onClose} visible={visibility}>
      {projectId && taskId && isFieldsLoaded ? (
        <DrawerContent
          projectId={projectId}
          taskId={taskId}
          onClose={onClose}
        />
      ) : (
        <LoadingContainer>
          <HeaderContentLoader />
        </LoadingContainer>
      )}
    </Drawer>
  );
};
